/**
 * DemoWelcomePage - Welcome/Thank You Page after Demo Registration
 *
 * Separate from DeveloperWelcomePage to allow independent customization.
 * Same layout structure but with demo-specific messaging.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { CheckCircle2 } from 'lucide-react';
import { AnonymousPortalLayout } from '@/components/common/AnonymousPortalLayout';
import { ECGridColors } from '@/types/colors';

// ============================================================================
// Props
// ============================================================================

export interface DemoWelcomePageProps {
  subdomain?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export const DemoWelcomePage = React.forwardRef<HTMLDivElement, DemoWelcomePageProps>(
  ({ subdomain }, ref) => {
    const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);

  return (
    <AnonymousPortalLayout ref={ref}>
      <div className="w-full max-w-2xl space-y-6">
          {/* Welcome Card */}
          <div className="bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 sm:p-8">
            {/* Success Header */}
            <div className="text-center mb-8">
              {/* Success Icon */}
              <div className="mb-4">
                <div
                  className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${ECGridColors.blue}15` }}
                >
                  <CheckCircle2 className="h-8 w-8" style={{ color: ECGridColors.blue }} />
                </div>
              </div>

              {/* ECGrid Logo */}
              <div className="mb-4">
                <img
                  src="/img/ecgridlogo2x.png"
                  alt="ECGrid"
                  className="h-12 mx-auto"
                />
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: ECGridColors.tealDark }}>
                {t('DemoWelcome_Title', 'Welcome to the ECGrid Demo Program')}
              </h1>

              {/* Subtitle */}
              <p className="text-lg sm:text-xl mb-8" style={{ color: ECGridColors.blue }}>
                {t('DemoWelcome_Subtitle', 'Your demo account is ready!')}
              </p>
            </div>

            {/* Content Paragraphs */}
            <div className="space-y-6 text-gray-700">
              {/* Paragraph 1 - Introduction */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DemoWelcome_Paragraph1',
                  'Welcome to the ECGrid Demo Program. Your sandbox environment has been provisioned and is ready for you to explore. The demo account gives you access to the ECGrid portal where you can experience our B2B connectivity infrastructure firsthand.'
                )}
              </p>

              {/* Paragraph 2 - Credentials Email */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DemoWelcome_Paragraph2',
                  'A secure message containing your login credentials has been sent to your email address. If this communication does not appear in your inbox, please verify your junk or spam folders. Should you require further assistance, our support team is available at support@ecgrid.com to ensure you have immediate access to your environment.'
                )}
              </p>

              {/* Paragraph 3 - Demo Limitations */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DemoWelcome_Paragraph3',
                  'Your demo account includes access to the ECGrid portal with a limited mailbox capacity. This allows you to test core functionality and evaluate how ECGrid can integrate with your platform. Detailed operational guidance is available through our Knowledge Base at '
                )}
                <a
                  href="https://support.ecgrid.com/support/home"
                  className="font-medium hover:underline"
                  style={{ color: ECGridColors.blue }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://support.ecgrid.com/support/home
                </a>
              </p>

              {/* Paragraph 4 - Upgrade Path */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t(
                  'DemoWelcome_Paragraph4',
                  'When you are ready to upgrade to a full ECGrid Developer or Production account, please reach out to your account representative or contact our sales team. We will be happy to assist you in selecting the right plan for your needs.'
                )}
              </p>

              {/* Paragraph 5 - Support Contact */}
              <p className="text-sm sm:text-base leading-relaxed">
                {t('DemoWelcome_Paragraph5', 'Our knowledgeable support team can be reached by emailing')}{' '}
                <a
                  href="mailto:support@ecgrid.com"
                  className="font-medium hover:underline"
                  style={{ color: ECGridColors.blue }}
                >
                  support@ecgrid.com
                </a>
                .
              </p>

              {/* Portal Link Section - Only shown when subdomain is available */}
              {subdomain && (
                <div className="my-8 p-6 rounded-lg border-2 text-center"
                     style={{
                       borderColor: ECGridColors.blue,
                       backgroundColor: `${ECGridColors.blue}08`
                     }}>
                  <p className="text-base sm:text-lg font-semibold mb-4"
                     style={{ color: ECGridColors.tealDark }}>
                    {t('DemoWelcome_PortalLink_Title', 'Your Demo Portal is Ready')}
                  </p>
                  <a
                    href={`https://${subdomain}.ecgrid.com`}
                    className="inline-block px-8 py-3 rounded-lg text-white font-semibold text-base sm:text-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg"
                    style={{ backgroundColor: ECGridColors.blue }}
                  >
                    {t('DemoWelcome_PortalLink_Button', 'Go to Your Demo Portal')}
                  </a>
                  <p className="text-xs sm:text-sm mt-3 text-gray-600">
                    {subdomain}.ecgrid.com
                  </p>
                </div>
              )}

              {/* Paragraph 6 - Closing */}
              <p className="text-base sm:text-lg font-medium text-center mt-8" style={{ color: ECGridColors.tealDark }}>
                {t('DemoWelcome_Paragraph6', 'We look forward to helping you explore the full potential of ECGrid.')}
              </p>
            </div>
          </div>

      </div>
    </AnonymousPortalLayout>
  );
});

DemoWelcomePage.displayName = 'DemoWelcomePage';
